import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Button,
  Container,
  Skeleton,
  FormControl,
  InputLabel,
  OutlinedInput,
  Autocomplete,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from '../common/components/LocalizationProvider';
import { sessionActions } from '../store';
import ReportsMenu from './components/ReportsMenu';
import PageLayout from '../common/components/PageLayout';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
  },
  buttons: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-evenly',
    '& > *': {
      flexBasis: '33%',
    },
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
}));

const MobilityReportSettingsPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();

  const currentUser = useSelector((state) => state.session.user);
  const geofences = useSelector((state) => state.geofences.items);

  const geofencesOptions = Object.values(geofences || {});

  const [mobilityReportHomeThreshold, setMobilityReportHomeThreshold] = useState(currentUser?.attributes?.mobilityReportHomeThreshold || 24);
  const [mobilityReportAwayThreshold, setMobilityReportAwayThreshold] = useState(currentUser?.attributes?.mobilityReportAwayThreshold || 24);
  const [mobilityReportHomeGeofences, setMobilityReportHomeGeofences] = useState(currentUser?.attributes?.mobilityReportHomeGeofences || '');
  const [selectedGeofences, setSelectedGeofences] = useState([]);

  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  const [item] = useState(currentUser);

  const [isLoading, setIsLoading] = useState(false);

  const handleGeofencesChange = (event, newValues) => {
    setSelectedGeofences(newValues);

    if (newValues && newValues.length > 0) {
      const idsString = newValues.map((item) => item.id).join(',');

      setMobilityReportHomeGeofences(idsString);
    } else {
      setMobilityReportHomeGeofences('');
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      const response = await fetch('/api/reports/mobility', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobilityReportHomeThreshold,
          mobilityReportAwayThreshold,
          mobilityReportHomeGeofences,
        }),
      });

      const data = await response.json();

      dispatch(sessionActions.updateUser(data));

      navigate(-1);
    } catch {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (mobilityReportHomeGeofences && geofencesOptions.length > 0) {
      const geofenceIds = mobilityReportHomeGeofences.split(',').map((id) => parseInt(id.trim(), 10));
      const selected = geofencesOptions.filter((geofence) => geofenceIds.includes(geofence.id));

      setSelectedGeofences(selected);
    }
  }, [mobilityReportHomeGeofences, geofences]);

  return (
    <PageLayout
      menu={<ReportsMenu />}
      breadcrumbs={['reportTitle', 'reportMobility', 'reportSettings']}
    >
      <Container maxWidth="xs" className={classes.container}>
        {item ? (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                Judrumo ataskaitos nustatymai
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <FormControl>
                <InputLabel>Neaktyvumo slenkstis įmonės georibose</InputLabel>
                <OutlinedInput
                  label="Neaktyvumo slenkstis įmonės georibose"
                  type="text"
                  value={mobilityReportHomeThreshold === null || mobilityReportHomeThreshold === undefined ? '' : mobilityReportHomeThreshold.toString()}
                  onChange={(e) => {
                    const { value } = e.target;

                    if (/^\d*$/.test(value)) {
                      if (value === '') {
                        setMobilityReportHomeThreshold(null);
                      } else {
                        const numValue = parseInt(value, 10);
                        setMobilityReportHomeThreshold(numValue);
                      }
                    }
                  }}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                />
              </FormControl>
              <FormControl>
                <InputLabel>Neaktyvumo slenkstis klientų georibose</InputLabel>
                <OutlinedInput
                  label="Neaktyvumo slenkstis klientų georibose"
                  type="text"
                  value={mobilityReportAwayThreshold === null || mobilityReportAwayThreshold === undefined ? '' : mobilityReportAwayThreshold.toString()}
                  onChange={(e) => {
                    const { value } = e.target;

                    if (/^\d*$/.test(value)) {
                      if (value === '') {
                        setMobilityReportAwayThreshold(null);
                      } else {
                        const numValue = parseInt(value, 10);
                        setMobilityReportAwayThreshold(numValue);
                      }
                    }
                  }}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                />
              </FormControl>
              <Autocomplete
                multiple
                loading={!geofences}
                options={geofencesOptions}
                value={selectedGeofences}
                onChange={handleGeofencesChange}
                getOptionLabel={(item) => item?.name ?? ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label="Įmonės georibos" />
                )}
                open={autocompleteOpen}
                onClose={() => setAutocompleteOpen(false)}
                onOpen={() => {
                  setAutocompleteOpen(true);
                }}
              />
            </AccordionDetails>
          </Accordion>
        ) : (
          <Accordion defaultExpanded>
            <AccordionSummary>
              <Typography variant="subtitle1">
                <Skeleton width="10em" />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={-i} width="100%">
                  <TextField />
                </Skeleton>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
        <div className={classes.buttons}>
          <Button
            type="button"
            color="primary"
            variant="outlined"
            onClick={() => navigate(-1)}
            disabled={!item}
          >
            {t('sharedCancel')}
          </Button>
          <Button
            type="button"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={!item || isLoading}
          >
            {t('sharedSave')}
          </Button>
        </div>
      </Container>
    </PageLayout>
  );
};

export default MobilityReportSettingsPage;
